import { trpc } from '@tencent/tpro_rmonitor_web_server_exception';
import { get } from 'lodash-es';
import { TRPCPost } from '@/infrastructure/axios';
import { API_PREFIX, BusiCode } from '@/infrastructure/axios/code';
import AbstractHttpClient from '@/infrastructure/axios/request';
import { logger } from '@/infrastructure/logger';
import { ClusterStackType } from '@/presentation/components/form/proto';
import { IGcDetail, IGcStatistic } from '@/presentation/components/problem-detail-gc/types';
import { IssueType } from '@/proto/issue';
import { IUserKey } from '@/proto/user';
import { TrpcService } from '@/service/trpc';
import { WithTypeCast } from '@raft/utils';
import IGetLogFrequencyReq = trpc.rmonitor.web_server.IGetLogFrequencyReq;

type IGetDetailReq = WithTypeCast<trpc.rmonitor.web_server.IGetDetailReq>;
type IProblemDetail = WithTypeCast<trpc.rmonitor.web_server.IProblemDetail> & {
  gcInfo?: {
    gcDetail?: IGcDetail[];
    gcStatistic?: IGcStatistic;
    isNewSdkHasGc?: boolean;
  };
};
type IGetDetailRsp = WithTypeCast<trpc.rmonitor.web_server.IGetDetailRsp>;
export type IAttachment = WithTypeCast<trpc.rmonitor.web_server.IAttachment>;
type IGetLogFrequencyRsp = WithTypeCast<trpc.rmonitor.web_server.IGetLogFrequencyRsp>;

// eslint-disable-next-line max-len
export type IGetTranslatedStackFromMinidumpReq = WithTypeCast<trpc.rmonitor.web_server.IGetTranslatedStackFromMinidumpReq>;
// eslint-disable-next-line max-len
export type IGetTranslatedStackFromMinidumpRsp = WithTypeCast<trpc.rmonitor.web_server.IGetTranslatedStackFromMinidumpRsp>;
export type IFrame = WithTypeCast<trpc.rmonitor.web_server.IFrame>;
export type IGetReportListReq = WithTypeCast<trpc.rmonitor.web_server.IGetReportListReq>;
export type IGetReportListRsp = WithTypeCast<trpc.rmonitor.web_server.IGetReportListRsp>;
export type IReportInfo = WithTypeCast<trpc.rmonitor.web_server.IReportInfo>;
export type INote = WithTypeCast<trpc.rmonitor.web_server.INote>;
export type IGetUserClassifyDataKeysReply = WithTypeCast<trpc.rmonitor.web_server.IGetUserClassifyDataKeysReply>;
export type IGetUserClassifyDataKeysRequest = WithTypeCast<trpc.rmonitor.web_server.IGetUserClassifyDataKeysRequest>;
export type IGetCustomFieldKeysRequest = WithTypeCast<trpc.rmonitor.web_server.IGetCustomFieldKeysRequest>;
export type IGetCustomFieldKeysReply = WithTypeCast<trpc.rmonitor.web_server.IGetCustomFieldKeysReply>;

type LogRequest = {
  attachments: IAttachment[]
};

export interface IAttachmentList {
  clientIdentify: string,
  accountId: string,
  attachList: IAttachment[]
}
export interface IAttachmentDetail {
  threadName?: string,
  expName?: string,
  exceptionMsg?: string,
  retraceExcept?: string,
  retraceResult?: string,
}

export interface IFileList {
  content: IAttachment[],
  clientIdentify: string,
  detail: IAttachmentDetail,
}

/**
 * 崩溃详情
 */
export class ExceptionService extends AbstractHttpClient {
  constructor() {
    super({
      serviceName: TrpcService.ExceptionService,
    });
  }

  /**
   * 详情列表
   */
  public async getReportList(req: IGetReportListReq): Promise<IGetReportListRsp> {
    try {
      const res = await this.trpcPost<IGetReportListRsp>('GetReportList', req);
      if (res?.baseRsp?.code !== BusiCode.Ok) {
        return {};
      }
      return res;
    } catch (e) {
      return {};
    }
  }

  /**
   * 获取issue详情
   * @param req
   */
  public async getDetail(req: IGetDetailReq): Promise<IProblemDetail | null> {
    try {
      const res = await this.trpcPost<IGetDetailRsp>('GetDetail', req);
      if (res?.baseRsp?.code !== BusiCode.Ok) {
        return null;
      }
      const detail = get(res, 'detail', null);
      return {
        ...detail,
        gcInfo: {
          gcDetail: res.gcs || [],
          gcStatistic: res.statistic || {},
          isNewSdkHasGc: res.isNewSdkHasGc || false,
        },
      };
    } catch (e) {
      return null;
    }
  }

  public async getCrashFiles(req: {
    /** GetDetailReq issueType */
    issueType?: IssueType;
    /** GetDetailReq feature */
    feature?: (string|null);
    /** GetDetailReq clientIdentify */
    clientIdentify?: (string|null);
    /** GetDetailReq clusterStackType */
    clusterStackType?: ClusterStackType;
    platformId?: string;
  }): Promise<IAttachment[]> {
    try {
      const res = await TRPCPost(`/${API_PREFIX}/api/crash/files`, {
        ...req,
      });

      const files = res?.data ?? [];

      return files;
    } catch (e) {
      return [];
    }
  }

  public async getCrashFileLists(req: {
    /** GetDetailReq issueType */
    issueType?: IssueType;
    /** GetDetailReq feature */
    feature?: (string|null);
    /** GetDetailReq productId  */
    productId?: string;
    /** GetDetailReq clusterStackType */
    clusterStackType?: ClusterStackType;
    platformId?: string;
    clientIdentifies?: string[];
  }): Promise<IFileList[]> {
    try {
      const res = await TRPCPost(`/${API_PREFIX}/api/crash/fileLists`, {
        ...req,
      });

      return res.data;
    } catch (e) {
      return [
        {
          content: [],
          clientIdentify: '',
          detail: {},
        },
      ];
    }
  }

  /**
   *
   * @param req
   * @returns
   */
  public async getFileLists(req: {
    attachments: IAttachment[];
  }): Promise<IAttachment[]> {
    try {
      const res = await TRPCPost(`/${API_PREFIX}/api/crash/filelists`, {
        ...req,
      });

      const files = res?.data ?? [];

      return files;
    } catch (e) {
      return [];
    }
  }

  public async getDropFrameFiles(req: {
    /** GetDetailReq feature */
    feature?: (string|null);
    /** GetDetailReq clientIdentify */
    clientIdentify?: (string|null);
    /** GetDetailReq productId  */
    productId?: string;
  }): Promise<IAttachment[]> {
    try {
      const res = await TRPCPost(`/${API_PREFIX}/api/crash/dropfiles`, {
        ...req,
      });

      const files = res?.data ?? [];

      return files;
    } catch (e) {
      return [];
    }
  }

  public async getLogs(req: LogRequest): Promise<IAttachment[]> {
    try {
      const res = await this.client.post(
        `/${API_PREFIX}/api/crash/logs`,
        req,
      );

      return res.data ?? [];
    } catch (e) {
      return [];
    }
  }

  /**
   * 获取issue下满足查询条件下的日志文件的词频
   * @param req
   */
  public async getLogFrequency(req: IGetLogFrequencyReq): Promise<IGetLogFrequencyRsp | undefined> {
    try {
      return  await this.trpcPost<IGetLogFrequencyRsp>('GetLogFrequency', req);
    } catch (e) {
      return undefined;
    }
  }

  public async getTranslatedMinidumpStack(req:
  IGetTranslatedStackFromMinidumpReq): Promise<IGetTranslatedStackFromMinidumpRsp> {
    try {
      return  await this.trpcPost<IGetTranslatedStackFromMinidumpRsp>('GetTranslatedStackFromMinidump', req);
    } catch (e) {
      return {

      };
    }
  }

  /**
   * 查询用户自定义数据key列表
   * @param req
   */
  public async getUserClassifyDataKeys(req: IGetUserClassifyDataKeysRequest):
  Promise<IGetUserClassifyDataKeysReply> {
    try {
      return  await this.trpcPost<IGetTranslatedStackFromMinidumpRsp>('GetUserClassifyDataKeys', req);
    } catch (e) {
      return {

      };
    }
  }

  public async getCustomFieldKeys(req: IGetCustomFieldKeysRequest): Promise<IUserKey[]> {
    try {
      const resp = await this.trpcPost<IGetCustomFieldKeysReply>('GetCustomFieldKeys', req);
      const { keys = [] } = resp;
      return keys?.map(key => ({
        label: key.key,
        key: key.key,
        type: key.type,
      })) || [];
    } catch (e) {
      logger.error('getCustomFieldKeys:', e);
      return [];
    }
  }
}

export const exceptionService = new ExceptionService();
